<template>
  <div class="container mt-4">
    <div class="row">
      <h1 class="text-center">
        <span class="text-danger">PLIC</span>:
        Database of 3D Aligned <span class="text-danger">P</span>-<span class="text-danger">L</span>oop
        <span class="text-danger">I</span>on <span class="text-danger">C</span>hannels
        with universal residue Labels
      </h1>
      <p class="text-center">
        The superfamily of <span class="fw-bold text-warning">P-loop ion channels</span>
        includes hundreds of integral membrane proteins that
        play key roles in many physiological processes. Here we introduce a universal scheme of residue
        labels leveraging structural similarity of P-loop channels. The channels are 3D aligned to
        facilitate their structural comparison. Structure of each channel is presented with universal and
        genuine (PDB-deposited) residue labels. Monte Carlo Energy minimized structures are also
        presented.
      </p>
    </div>
    <div class="row">
      <div class="col-lg-2">
        <div id="main-page-nav" class="list-group sticky-top" style="position: sticky">
          <a class="list-group-item list-group-item-action" href="#purpose">Purpose</a>
          <a class="list-group-item list-group-item-action" href="#labeling">Labeling</a>
          <a class="list-group-item list-group-item-action" href="#processing">Processing 3D structures</a>
          <a class="list-group-item list-group-item-action" href="#usage">Usage</a>
          <a class="list-group-item list-group-item-action" href="#tools">Tools</a>
          <a class="list-group-item list-group-item-action" href="#download">Download</a>
          <a class="list-group-item list-group-item-action" href="#feedback">Feedback</a>
        </div>
      </div>
      <div class="col-lg-10">
        <div class="container">
          <h2 id="purpose">Purpose</h2>
          <p>The superfamily of P-loop channels includes potassium, sodium, calcium, cyclic-nucleotide gated,
            two-pore, and transit receptor potential channels, as well as ionotropic glutamate receptors.
            Despite
            the large functional diversity, all members of the superfamily have conserved folding of
            transmembrane
            helices in the tetrameric or pseudo-tetrameric pore-forming module where each monomer has either
            two
            (2TM) or six (6-TM) transmembrane segments. 6-TM channels also have conserved folding of
            voltage-sensing
            domains. Hundreds of atomic-scale structures of P-loop channels obtained by X-ray
            crystallography
            and
            cryo-electron microscopy are deposited in the Protein Data Bank and the number of structures
            rapidly
            increases. Comparative structural analysis of P-loop channels is desirable, but it is hindered
            due
            different residue numbers, ambiguous sequence alignments, and different 3D orientation of the
            structures. To overcome these problems, we elaborated a universal residue-labeling scheme for
            P-loop
            channels and sequence alignments of transmembrane segments that is consistent with this
            scheme.</p>

          <h2 id="labeling">Residue labeling</h2>
          <p>A universal residue labeling in TM segments is a key feature of our database that may facilitate
            structural comparison of the vastly different P-loop channels (Figure). P-loop channels are
            tetramers or
            pseudo-tetramers. Four subunits in the tetrameric channels are labeled as chains A to D, like in
            many
            PDB structures. In pseudo-tetrameric channels, such as sodium and calcium channels, repeats I to
            IV
            of
            the pore-forming α subunit are also labeled as chains A, B, C, and D. Ancillary subunits of
            these
            channels (if any) are labeled as chains E, F, G, etc. in the order they appear in the original
            PDB
            file.
            Ions are labeled as chain I (ion), low-molecular weight ligands are labeled as chain L
            (ligands).
            Water
            molecules are labeled as chain W. Residues in the ancillary subunits are numbered as in the
            original
            PDB
            file. Ligands, ions, and waters are numbered sequentially, as they appear in the PDB file. In
            all
            tetrameric channels, chains A to D are arranged clockwise when viewed from the extracellular
            space
            to
            match the arrangement of repeats I to IV, respectively, in sodium channels.</p>

          <div style="text-align: center">
            <img :src="referenceImageUrl" alt="Transmembrane topology" class="img-fluid">
            <div>
              <strong>Transmembrane topology and reference positions of conserved residues in a
                repeat/subunit
                of
                6TM
                channels</strong>
            </div>
          </div>

          <p>A residue label in a TM segment of the pore domain consists of two parts. The first part contains
            a
            letter indicating chain A-D, which may be omitted and number 1 to 6 that indicate, respectively,
            segments S1 to S6. Number 7 refers to the long chain C-terminal to S6, which is seen in some
            channels,
            e.g., inter-repeat cytoplasmic linkers or C-terminal parts. Three digits in the second part of
            the
            label
            indicate a relative residue number. The numbers are counted from the most conserved residues in
            the
            TM
            segment, which is assigned number 550. The big numbers are chosen with the aim to accommodate
            extra-
            and
            intracellular loops, which are very large in some channels, or the N-terminal chain. P-loops are
            not
            TM
            segments. In 6-TM channels they are located between segments S5 and S6. In 2TM channels P-loops
            are
            located between segments S1 and S2. We assigned number 5.850 to the valine residue in the
            conserved
            motif TVGYG (the signature sequence) in the selectivity filter of potassium channels. Since
            loops
            are
            vastly different, no attempt was made to perform their sequence alignment. N-ends of the
            intracellular
            and extracellular loops S1-S2, S2-S3, S3-S4, S5-P1 and P2-S6 are assigned, respectively, numbers
            1.600,
            2.600, 3.600, 5.600 and 5.900 (Figure). Sequence alignment of TM segments was initially
            performed by
            Clustal Omega and was manually corrected. First, we avoided insertions and deletions in the
            alignment
            within helical segments. Corrections were also made to provide the same orientation of the helix
            residues in homologous positions.

            We are unaware of P-loop channels whose residue labels would not fit into this general scheme.
          </p>

          <h2 id="processing">Processing 3D structures.</h2>

          <p>
            Original data are downloaded from the Protein Data Bank (www.rcsb.org). The PDB files deposited
            as
            "Biological assembly" were used. To obtain 3D-aligned structures we used as a template the X-ray
            structure 2R9R of the Kv1.2-Kv2.1 chimeric potassium channel in the open state. The structure
            was
            initially reoriented in such a way that the pore axis coincided with the z axis and whose
            coordinates
            increased in the extracellular direction. The xOy plane accommodated the backbone oxygens in
            position
            5.850. Other structures were aligned to the reference structure 2R9R by minimizing RMS
            deviations
            between alpha carbons in positions 5.838 – 5.847 in the P1 helix, which is the most structurally
            conserved element in P-loop channels. This strategy of 3D alignment seems to have significant
            advantage
            over the alignment of full-length channels. In the latter case in may be difficult to locate in
            3D
            aligned structures rather subtle, but functionally significant differences.
          </p>

          <h2 id="usage">Usage</h2>

          <p>
            The relabeled and 3D-aligned structures are grouped in the folders according to the channels
            families.
            The structures can be viewed at the site or downloaded from it. Each entry also contains the
            references
            to original structure at the Protein Data Bank and to the original publication at PubMed.
            There are three ways to find the structures.
            1. Direct catalog browsing (left panel of the navigation page).
            2. Tags that extract the corresponding group of structures (right pane on the navigation page).
            3. Search engine (top of the navigation page).
          </p>

          <h2 id="tools">Tools</h2>

          <p>The database contains a utility that allows a user to renumber and 3D-align new structures of
            P-loop
            channels.</p>

          <h2 id="download">Download</h2>

          <p>You can download our alignment in an Excel format via this <a href="/plic.xlsx">link</a>!</p>

          <h2 id="feedback">Feedback</h2>

          <p>The authors welcome comments, corrections and suggestions. New structures can be included by
            request.
            You can learn more about us and how to contact us <router-link to="/about"> here</router-link>.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const referenceImageUrl = '/images/reference.jpg'

export default {
  name: 'Home',
  data() {
    return {
      referenceImageUrl
    }
  }
}
</script>
